import { useState, useEffect, ChangeEvent } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { CaseExternalModel } from 'models';
import services from 'services';

import { ExternalDetails } from 'components';

type View = 'base' | 'complete' | 'cancel';
type Method = 'reject' | 'resolve';

function getWarningForCompleteForm(isFromTask: boolean) {
  return 'Ilmoita ratkaisun kuvaus';
}

function getWarningForCancelForm(isFormTask: boolean) {
  if (isFormTask) {
    return 'Ilmoita ratkaisun kuvaus';
  }

  return 'Ilmoita peruuttamisen syy';
}

interface LocalState {
  externalCase: CaseExternalModel | null;
  loading: boolean;
  error: boolean;
  resolve: boolean;
  reject: boolean;
  isValidationError: boolean;
  view: View;
  text: string;
}

const External = () => {
  const location = useLocation();
  const history = useHistory();
  const [state, setState] = useState<LocalState>({
    externalCase: null,
    loading: false,
    error: false,
    resolve: false,
    reject: false,
    isValidationError: false,
    view: 'base',
    text: '',
  });

  const fetchExternalCase = async () => {
    try {
      setState((prevState) => ({ ...prevState, loading: true, error: false }));

      const externalCase: CaseExternalModel = (
        await services.get(`/api/cases/external${location.search}`)
      ).data.Data;

      setState((prevState) => ({
        ...prevState,
        externalCase,
        loading: false,
      }));
    } catch (err) {
      console.error(err);

      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    if (!location.search) {
      history.push('/');

      return;
    }

    fetchExternalCase();

    /* eslint-disable-next-line */
  }, []);

  const putCase = async (method: Method) => {
    setState((prevState) => ({ ...prevState, loading: true }));

    try {
      await services.put(
        `/api/cases/external/${method}${location.search}&description=${state.text}`
      );

      setState((prevState) => ({
        ...prevState,
        loading: false,
        [method]: true,
      }));
    } catch (err) {
      console.error(err);

      setState((prevState) => ({ ...prevState, loading: false, error: true }));
    }
  };

  const handleCompleteCase = () => {
    const warning = getWarningForCompleteForm(
      state.externalCase?.IsFromTask ?? false
    );
    const isValid = !!state.text && state.text !== warning;

    if (isValid) {
      putCase('resolve');
    } else {
      setState((prevState) => ({
        ...prevState,
        isValidationError: true,
        text: warning,
      }));
    }
  };

  const handleRejectCase = () => {
    const warning = getWarningForCancelForm(
      state.externalCase?.IsFromTask ?? false
    );
    const isValid = !!state.text && state.text !== warning;

    if (isValid) {
      putCase('reject');
    } else {
      setState((prevState) => ({
        ...prevState,
        isValidationError: true,
        text: warning,
      }));
    }
  };

  const initValidation = () => {
    if (state.isValidationError) {
      setState((prevState) => ({
        ...prevState,
        isValidationError: false,
        text: '',
      }));
    }
  };

  const handleChangeText = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;

    setState((prevState) => ({
      ...prevState,
      text: value,
    }));
  };

  const handleChangeView = (view: View) => {
    setState((prevState) => ({
      ...prevState,
      view,
      isValidationError: false,
      text: '',
    }));
  };

  return (
    <ExternalDetails
      {...state}
      handleCompleteCase={handleCompleteCase}
      handleRejectCase={handleRejectCase}
      handleChangeText={handleChangeText}
      handleChangeView={handleChangeView}
      initValidation={initValidation}
    />
  );
};

export default External;
